import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { CookieConsent } from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import { ElevenLabsLocalizer } from "@/components/ElevenLabsLocalizer";

const Index = lazy(() => import("./pages/Index"));
const Diagnostic = lazy(() => import("./pages/Diagnostic"));
const About = lazy(() => import("./pages/About"));
const News = lazy(() => import("./pages/News"));
const SuccessStories = lazy(() => import("./pages/SuccessStories"));
const TechStack = lazy(() => import("./pages/TechStack"));
const Gracias = lazy(() => import("./pages/Gracias"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Partners = lazy(() => import("./pages/Partners"));
const Services = lazy(() => import("./pages/Services"));
const DesignSystem = lazy(() => import("./pages/DesignSystem"));
const TypographyTest = lazy(() => import("./pages/TypographyTest"));
const ProtagorasColbai = lazy(() => import("./pages/ProtagorasColbai"));

// Lazy load legal pages wrapper or individual exports? 
// The original import was named imports. Let's lazily import them.
// Since they are named exports from "./pages/Legal", we can do:
const LegalNotice = lazy(() => import("./pages/Legal").then(module => ({ default: module.LegalNotice })));
const PrivacyPolicy = lazy(() => import("./pages/Legal").then(module => ({ default: module.PrivacyPolicy })));
const CookiesPolicy = lazy(() => import("./pages/Legal").then(module => ({ default: module.CookiesPolicy })));
const SecurityPolicy = lazy(() => import("./pages/Legal").then(module => ({ default: module.SecurityPolicy })));

const queryClient = new QueryClient();

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <Loader2 className="w-8 h-8 text-primary animate-spin" />
  </div>
);

import { LangLayout } from "@/components/LangLayout";

const App = () => {
  console.log("App rendered, routes initialized");

  const appRoutes = [
    { path: "", element: <Index /> },
    { path: "diagnostic", element: <Diagnostic /> },
    { path: "about", element: <About /> },
    { path: "news", element: <News /> },
    { path: "success-stories", element: <SuccessStories /> },
    { path: "tech-stack", element: <TechStack /> },
    { path: "gracias", element: <Gracias /> },
    { path: "partners", element: <Partners /> },
    { path: "services", element: <Services /> },
    { path: "design-system", element: <DesignSystem /> },
    { path: "typography-test", element: <TypographyTest /> },
    { path: "protagoras", element: <ProtagorasColbai /> },
    { path: "protagorasxcolbai", element: <Navigate to="../protagoras" replace /> },
    { path: "nota-legal", element: <LegalNotice /> },
    { path: "privacidad", element: <PrivacyPolicy /> },
    { path: "cookies", element: <CookiesPolicy /> },
    { path: "seguridad", element: <SecurityPolicy /> },
  ];

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <ElevenLabsLocalizer />
          <CookieConsent />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Default Language Routes (No prefix) */}
              <Route path="/" element={<LangLayout />}>
                {appRoutes.map((route) => (
                  <Route key={`root-${route.path}`} path={route.path} element={route.element} />
                ))}
              </Route>

              {/* Language Prefixed Routes */}
              <Route path="/:lang" element={<LangLayout />}>
                {appRoutes.map((route) => (
                  <Route key={`lang-${route.path}`} path={route.path} element={route.element} />
                ))}
              </Route>

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
